import "regenerator-runtime/runtime";


/*
|--------------------------------------------------------------------------
| API HELPER
|--------------------------------------------------------------------------
|
| Supports:
|
| - Tenant accessToken
| - Platform platformAccessToken
| - JSON requests
| - URL encoded requests
| - Multipart uploads
| - Query parameters
| - DELETE body
| - File downloads
| - Old boolean authentication syntax
| - New options syntax
|
|--------------------------------------------------------------------------
*/


class API {

  constructor({
    tokenKey = "accessToken",
  } = {}) {

    this.tokenKey =
      tokenKey;

  }


  /*
  |--------------------------------------------------------------------------
  | GET TOKEN
  |--------------------------------------------------------------------------
  */

  async getToken() {

    if (
      typeof window ===
      "undefined"
    ) {

      return "";

    }


    return (
      localStorage
        .getItem(
          this.tokenKey
        )
        ?.trim() ||
      ""
    );

  }


  /*
  |--------------------------------------------------------------------------
  | NORMALIZE AUTH OPTIONS
  |--------------------------------------------------------------------------
  |
  | Supports:
  |
  | api.get(url, true)
  |
  | api.get(url, {
  |   authentication: true
  | })
  |
  | api.get(url, {
  |   authentication: true,
  |   tokenKey: "platformAccessToken"
  | })
  |
  |--------------------------------------------------------------------------
  */

/*
|--------------------------------------------------------------------------
| NORMALIZE AUTH OPTIONS
|--------------------------------------------------------------------------
*/

normalizeAuthOptions(
  options = {}
) {

  /*
  |--------------------------------------------------------------------------
  | OLD BOOLEAN FORMAT
  |--------------------------------------------------------------------------
  */

  if (
    typeof options ===
    "boolean"
  ) {

    return {

      authentication:
        options !==
        false,

      tokenKey:
        this.tokenKey,

    };

  }


  /*
  |--------------------------------------------------------------------------
  | SKIP AUTH
  |--------------------------------------------------------------------------
  */

  if (
    options
      ?.skipAuth ===
    true
  ) {

    return {

      authentication:
        false,

      tokenKey:
        options
          ?.tokenKey ||
        this.tokenKey,

    };

  }


  /*
  |--------------------------------------------------------------------------
  | DEFAULT = AUTHENTICATED
  |--------------------------------------------------------------------------
  */

  return {

    authentication:
      options
        ?.authentication ===
        false
        ?
        false
        :
        true,

    tokenKey:
      options
        ?.tokenKey ||
      this.tokenKey,

  };

}

  /*
  |--------------------------------------------------------------------------
  | GET AUTH TOKEN
  |--------------------------------------------------------------------------
  */

async getAuthToken(
  options = {}
) {

  if (
    typeof window ===
    "undefined"
  ) {

    return "";

  }


  const {
    authentication,
    tokenKey,
  } =
    this.normalizeAuthOptions(
      options
    );


  if (
    !authentication
  ) {

    return "";

  }


  return (
    localStorage
      .getItem(
        tokenKey
      )
      ?.trim() ||
    ""
  );

}


  /*
  |--------------------------------------------------------------------------
  | BUILD AUTH HEADERS
  |--------------------------------------------------------------------------
  */

async buildAuthHeaders(
  options = {}
) {

  const token =
    await this.getAuthToken(
      options
    );


  if (
    !token
  ) {

    return {};

  }


  return {

    Authorization:
      `Bearer ${token}`,

  };

}

  /*
  |--------------------------------------------------------------------------
  | PARSE RESPONSE
  |--------------------------------------------------------------------------
  */

  async parseResponse(
    response
  ) {

    /*
    |--------------------------------------------------------------------------
    | NO RESPONSE
    |--------------------------------------------------------------------------
    */

    if (
      !response
    ) {

      return {

        success:
          false,

        status:
          "failed",

        code:
          500,

        message:
          "No response received from server.",

      };

    }


    /*
    |--------------------------------------------------------------------------
    | CONTENT TYPE
    |--------------------------------------------------------------------------
    */

    const contentType =
      response
        .headers
        ?.get(
          "content-type"
        ) ||
      "";


    /*
    |--------------------------------------------------------------------------
    | JSON RESPONSE
    |--------------------------------------------------------------------------
    */

    if (
      contentType.includes(
        "application/json"
      )
    ) {

      try {

        const data =
          await response.json();


        /*
        |--------------------------------------------------------------------------
        | Keep backend response structure unchanged
        |--------------------------------------------------------------------------
        */

        return data;

      } catch (
        error
      ) {

        console.error(
          "Unable to parse JSON response:",
          error
        );


        return {

          success:
            false,

          status:
            "failed",

          code:
            response.status,

          message:
            "Unable to parse server response.",

        };

      }

    }


    /*
    |--------------------------------------------------------------------------
    | EMPTY RESPONSE
    |--------------------------------------------------------------------------
    */

    if (
      response.status ===
      204
    ) {

      return {

        success:
          true,

        status:
          "success",

        code:
          204,

        message:
          "Request completed successfully.",

        data:
          null,

      };

    }


    /*
    |--------------------------------------------------------------------------
    | TEXT RESPONSE
    |--------------------------------------------------------------------------
    */

    try {

      const text =
        await response.text();


      if (
        response.ok
      ) {

        return {

          success:
            true,

          status:
            "success",

          code:
            response.status,

          message:
            text ||
            "Request completed successfully.",

          data:
            text,

        };

      }


      return {

        success:
          false,

        status:
          "failed",

        code:
          response.status,

        message:
          text ||
          response.statusText ||
          "Request failed.",

      };

    } catch (
      error
    ) {

      console.error(
        "Unable to parse response:",
        error
      );


      return {

        success:
          false,

        status:
          "failed",

        code:
          response.status,

        message:
          response.statusText ||
          "Request failed.",

      };

    }

  }


  /*
  |--------------------------------------------------------------------------
  | GLOBAL TENANT SUBSCRIPTION RESPONSE HANDLER
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  |
  | We intentionally DO NOT import Redux store here.
  |
  | apiCalling.js is used by many Redux slices.
  |
  | Importing the Redux store inside this helper can create:
  |
  | API
  |  ↓
  | Redux Slice
  |  ↓
  | Store
  |  ↓
  | API
  |
  | circular dependency.
  |
  | Instead we dispatch browser events.
  |
  |--------------------------------------------------------------------------
  */

/*
|--------------------------------------------------------------------------
| GLOBAL RESPONSE HANDLER
|--------------------------------------------------------------------------
|
| Handles:
|
| 1. Tenant session expiry
| 2. Subscription module unavailable
| 3. Subscription required / expired / suspended
|
|--------------------------------------------------------------------------
*/

handleGlobalResponse({
    response,
    url,
    httpStatus = null,
    authentication = false,
    tokenKey = this.tokenKey,
} = {}) {

    /*
    |--------------------------------------------------------------------------
    | BROWSER ONLY
    |--------------------------------------------------------------------------
    */

    if (
        typeof window ===
        "undefined"
    ) {

        return;

    }


    /*
    |--------------------------------------------------------------------------
    | INVALID RESPONSE
    |--------------------------------------------------------------------------
    */

    if (
        !response ||
        typeof response !==
        "object"
    ) {

        return;

    }


    const requestUrl =
        String(
            url ||
            ""
        );


    /*
    |--------------------------------------------------------------------------
    | NORMALIZE HTTP STATUS
    |--------------------------------------------------------------------------
    */

    const responseCode =
        Number(
            httpStatus ||
            response?.code ||
            response?.statusCode ||
            0
        );


    /*
    |--------------------------------------------------------------------------
    | TENANT SESSION EXPIRED
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | Only tenant authenticated requests should trigger tenant logout.
    |
    | We do NOT use message text like:
    |
    | "Your session has expired..."
    |
    | because HTTP 401 is the source of truth.
    |
    |--------------------------------------------------------------------------
    */

    const isTenantRequest =
        authentication ===
            true &&
        String(
            tokenKey ||
            ""
        ) ===
            "accessToken";


    const isTenantAuthEndpoint =
        requestUrl.includes(
            "/v1/admin/auth/login"
        ) ||
        requestUrl.includes(
            "/v1/admin/auth/register"
        ) ||
        requestUrl.includes(
            "/v1/admin/auth/forgot-password"
        ) ||
        requestUrl.includes(
            "/v1/admin/auth/reset-password"
        );


    if (
        isTenantRequest &&
        !isTenantAuthEndpoint &&
        responseCode ===
            401
    ) {

        window.dispatchEvent(
            new CustomEvent(
                "comkwik:tenant-session-expired",
                {
                    detail: {

                        code:
                            401,

                        message:
                            response?.message ||
                            "Your session has expired. Please log in again.",

                        url:
                            requestUrl,

                    },
                }
            )
        );


        /*
        |--------------------------------------------------------------------------
        | STOP OTHER GLOBAL HANDLERS
        |--------------------------------------------------------------------------
        */

        return;

    }


    /*
    |--------------------------------------------------------------------------
    | IGNORE PLATFORM ADMIN API FOR TENANT SUBSCRIPTION HANDLING
    |--------------------------------------------------------------------------
    */

    if (
        requestUrl.includes(
            "/v1/platform/"
        )
    ) {

        return;

    }


    /*
    |--------------------------------------------------------------------------
    | IGNORE SUBSCRIPTION APIs THEMSELVES
    |--------------------------------------------------------------------------
    */

    if (
        requestUrl.includes(
            "/v1/admin/subscription"
        )
    ) {

        return;

    }


    /*
    |--------------------------------------------------------------------------
    | NORMALIZE ERROR CODE
    |--------------------------------------------------------------------------
    */

    const errorCode =
        String(
            response?.errorCode ||
            ""
        )
            .trim()
            .toUpperCase();


    /*
    |--------------------------------------------------------------------------
    | NORMALIZE REASON
    |--------------------------------------------------------------------------
    */

    const reason =
        String(
            response
                ?.data
                ?.reason ||
            response
                ?.reason ||
            ""
        )
            .trim()
            .toUpperCase();


    /*
    |--------------------------------------------------------------------------
    | MODULE NOT INCLUDED IN PLAN
    |--------------------------------------------------------------------------
    */

    if (
        errorCode ===
        "SUBSCRIPTION_MODULE_NOT_AVAILABLE"
    ) {

        window.dispatchEvent(
            new CustomEvent(
                "comkwik:subscription-upgrade-required",
                {
                    detail: {

                        moduleCode:
                            response
                                ?.data
                                ?.moduleCode ||
                            null,

                        message:
                            response
                                ?.message ||
                            "This feature is not available in your current subscription plan.",

                        upgradeRequired:
                            true,

                    },
                }
            )
        );


        return;

    }


    /*
    |--------------------------------------------------------------------------
    | SUBSCRIPTION BLOCK REASONS
    |--------------------------------------------------------------------------
    */

    const subscriptionReasons = [

        "NO_SUBSCRIPTION",

        "SUBSCRIPTION_REQUIRED",

        "SUBSCRIPTION_EXPIRED",

        "PAYMENT_PENDING",

        "SUBSCRIPTION_SUSPENDED",

        "SUBSCRIPTION_CANCELLED",

    ];


    /*
    |--------------------------------------------------------------------------
    | CHECK SUBSCRIPTION REQUIRED
    |--------------------------------------------------------------------------
    */

    const subscriptionRequired =

        errorCode ===
            "SUBSCRIPTION_REQUIRED"

        ||

        subscriptionReasons
            .includes(
                reason
            )

        ||

        response
            ?.data
            ?.subscriptionRequired ===
            true;


    if (
        !subscriptionRequired
    ) {

        return;

    }


    /*
    |--------------------------------------------------------------------------
    | SUBSCRIPTION EVENT
    |--------------------------------------------------------------------------
    */

    window.dispatchEvent(
        new CustomEvent(
            "comkwik:subscription-required",
            {
                detail: {

                    errorCode:
                        errorCode ||
                        null,

                    reason:
                        reason ||
                        null,

                    message:
                        response
                            ?.message ||
                        "",

                    subscription:
                        response
                            ?.data
                            ?.subscription ||
                        null,

                    subscriptionRequired:
                        true,

                },
            }
        )
    );

}

  /*
  |--------------------------------------------------------------------------
  | NORMALIZE REQUEST OPTIONS
  |--------------------------------------------------------------------------
  */

/*
|--------------------------------------------------------------------------
| NORMALIZE REQUEST OPTIONS
|--------------------------------------------------------------------------
|
| IMPORTANT:
|
| Existing ComKwik behavior:
|
| api.get(url)
|      -> SEND TOKEN
|
| api.post(url, body)
|      -> SEND TOKEN
|
| api.post(url, body, true)
|      -> SEND TOKEN
|
| api.post(url, body, false)
|      -> DO NOT SEND TOKEN
|
| api.post(url, body, {
|     skipAuth: true
| })
|      -> DO NOT SEND TOKEN
|
|--------------------------------------------------------------------------
*/

normalizeRequestOptions(
  options = {}
) {

  /*
  |--------------------------------------------------------------------------
  | OLD BOOLEAN FORMAT
  |--------------------------------------------------------------------------
  */

  if (
    typeof options ===
    "boolean"
  ) {

    return {

      authentication:
        options !==
        false,

      skipAuth:
        options ===
        false,

      tokenKey:
        this.tokenKey,

      headers:
        {},

      signal:
        undefined,

      credentials:
        undefined,

      mode:
        undefined,

      cache:
        undefined,

    };

  }


  /*
  |--------------------------------------------------------------------------
  | OBJECT FORMAT
  |--------------------------------------------------------------------------
  */

  const skipAuth =
    options
      ?.skipAuth ===
    true;


  const authentication =
    skipAuth
      ?
      false
      :
      options
        ?.authentication ===
        false
        ?
        false
        :
        true;


  return {

    authentication,

    skipAuth,

    tokenKey:
      options
        ?.tokenKey ||
      this.tokenKey,

    headers:
      options
        ?.headers ||
      {},

    signal:
      options
        ?.signal,

    credentials:
      options
        ?.credentials,

    mode:
      options
        ?.mode,

    cache:
      options
        ?.cache,

  };

}


  /*
  |--------------------------------------------------------------------------
  | BUILD QUERY STRING
  |--------------------------------------------------------------------------
  */

  buildQueryString(
    params = {}
  ) {

    if (
      !params ||
      typeof params !==
      "object"
    ) {

      return "";

    }


    const searchParams =
      new URLSearchParams();


    Object.keys(
      params
    ).forEach(
      (
        key
      ) => {

        const value =
          params[
            key
          ];


        /*
        |--------------------------------------------------------------------------
        | IGNORE NULL / UNDEFINED / EMPTY STRING
        |--------------------------------------------------------------------------
        */

        if (
          value ===
            undefined ||
          value ===
            null ||
          value ===
            ""
        ) {

          return;

        }


        /*
        |--------------------------------------------------------------------------
        | ARRAY
        |--------------------------------------------------------------------------
        */

        if (
          Array.isArray(
            value
          )
        ) {

          value.forEach(
            (
              item
            ) => {

              if (
                item !==
                  undefined &&
                item !==
                  null &&
                item !==
                  ""
              ) {

                searchParams.append(
                  key,
                  String(
                    item
                  )
                );

              }

            }
          );


          return;

        }


        /*
        |--------------------------------------------------------------------------
        | BOOLEAN / NUMBER / STRING
        |--------------------------------------------------------------------------
        */

        searchParams.append(
          key,
          String(
            value
          )
        );

      }
    );


    const query =
      searchParams
        .toString();


    return query
      ?
      `?${query}`
      :
      "";

  }


  /*
  |--------------------------------------------------------------------------
  | COMMON REQUEST
  |--------------------------------------------------------------------------
  */

  async request(
    method,
    url,
    {
      data = null,
      params = null,
      authentication = false,
      tokenKey = this.tokenKey,
      headers = {},
      signal,
      credentials,
      mode,
      cache,
      contentType = "application/json",
    } = {}
  ) {

    try {

      /*
      |--------------------------------------------------------------------------
      | AUTH HEADERS
      |--------------------------------------------------------------------------
      */

      const authHeaders =
        await this.buildAuthHeaders({

          authentication,

          tokenKey,

        });


      /*
      |--------------------------------------------------------------------------
      | REQUEST HEADERS
      |--------------------------------------------------------------------------
      */

      const requestHeaders = {

        ...authHeaders,

        ...headers,

      };


      /*
      |--------------------------------------------------------------------------
      | REQUEST OPTIONS
      |--------------------------------------------------------------------------
      */

      const requestOptions = {

        method,

        headers:
          requestHeaders,

      };


      /*
      |--------------------------------------------------------------------------
      | OPTIONAL FETCH OPTIONS
      |--------------------------------------------------------------------------
      */

      if (
        signal
      ) {

        requestOptions.signal =
          signal;

      }


      if (
        credentials
      ) {

        requestOptions.credentials =
          credentials;

      }


      if (
        mode
      ) {

        requestOptions.mode =
          mode;

      }


      if (
        cache
      ) {

        requestOptions.cache =
          cache;

      }


      /*
      |--------------------------------------------------------------------------
      | BODY
      |--------------------------------------------------------------------------
      */

      if (
        data !==
          null &&
        data !==
          undefined &&
        ![
          "GET",
          "HEAD",
        ].includes(
          String(
            method
          ).toUpperCase()
        )
      ) {

        /*
        |--------------------------------------------------------------------------
        | FORMDATA
        |--------------------------------------------------------------------------
        |
        | Do NOT manually set Content-Type.
        |
        | Browser adds multipart boundary automatically.
        |
        |--------------------------------------------------------------------------
        */

        if (
          typeof FormData !==
            "undefined" &&
          data instanceof
            FormData
        ) {

          requestOptions.body =
            data;

        }


        /*
        |--------------------------------------------------------------------------
        | URL ENCODED
        |--------------------------------------------------------------------------
        */

        else if (
          contentType ===
          "application/x-www-form-urlencoded"
        ) {

          requestHeaders[
            "Content-Type"
          ] =
            "application/x-www-form-urlencoded";


          requestOptions.body =
            new URLSearchParams(
              data
            ).toString();

        }


        /*
        |--------------------------------------------------------------------------
        | JSON
        |--------------------------------------------------------------------------
        */

        else {

          requestHeaders[
            "Content-Type"
          ] =
            "application/json";


          requestOptions.body =
            JSON.stringify(
              data
            );

        }

      }


      /*
      |--------------------------------------------------------------------------
      | QUERY STRING
      |--------------------------------------------------------------------------
      */

      const queryString =
        this.buildQueryString(
          params
        );


      /*
      |--------------------------------------------------------------------------
      | FETCH
      |--------------------------------------------------------------------------
      */

      const request =
        await fetch(
          `${url}${queryString}`,
          requestOptions
        );


      /*
      |--------------------------------------------------------------------------
      | PARSE RESPONSE
      |--------------------------------------------------------------------------
      */

      const response =
        await this.parseResponse(
          request
        );


      /*
      |--------------------------------------------------------------------------
      | GLOBAL SUBSCRIPTION RESPONSE HANDLING
      |--------------------------------------------------------------------------
      */

      this.handleGlobalResponse({

    response,

    url,

    httpStatus:
        request.status,

    authentication,

    tokenKey,

});


      /*
      |--------------------------------------------------------------------------
      | RETURN ORIGINAL BACKEND RESPONSE
      |--------------------------------------------------------------------------
      |
      | Important:
      |
      | Existing Redux slices/controllers continue receiving exactly the
      | same response object they received before this integration.
      |
      |--------------------------------------------------------------------------
      */

      return response;

    } catch (
      error
    ) {

      /*
      |--------------------------------------------------------------------------
      | ABORTED REQUEST
      |--------------------------------------------------------------------------
      */

      if (
        error
          ?.name ===
        "AbortError"
      ) {

        return {

          success:
            false,

          status:
            "cancelled",

          code:
            0,

          message:
            "Request cancelled.",

        };

      }


      console.error(
        "API request error:",
        error
      );


      /*
      |--------------------------------------------------------------------------
      | NETWORK ERROR
      |--------------------------------------------------------------------------
      */

      return {

        success:
          false,

        status:
          "failed",

        code:
          0,

        message:
          error
            ?.message ||
          "Unable to connect to server.",

        error,

      };

    }

  }

    /*
  |--------------------------------------------------------------------------
  | GET
  |--------------------------------------------------------------------------
  |
  | Supported:
  |
  | api.get(url)
  |
  | api.get(url, true)
  |
  | api.get(url, {
  |   authentication: true,
  |   tokenKey: "accessToken"
  | })
  |
  |--------------------------------------------------------------------------
  */

  async get(
    url,
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "GET",
      url,
      {

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | GET WITH QUERY PARAMS
  |--------------------------------------------------------------------------
  |
  | Example:
  |
  | api.getWithParams(
  |   url,
  |   {
  |     page: 1,
  |     search: "Dell"
  |   },
  |   true
  | )
  |
  |--------------------------------------------------------------------------
  */

  async getWithParams(
    url,
    params = {},
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "GET",
      url,
      {

        params,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | POST
  |--------------------------------------------------------------------------
  */

  async post(
    url,
    data = {},
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "POST",
      url,
      {

        data,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | PUT
  |--------------------------------------------------------------------------
  */

  async put(
    url,
    data = {},
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "PUT",
      url,
      {

        data,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | PATCH
  |--------------------------------------------------------------------------
  */

  async patch(
    url,
    data = {},
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "PATCH",
      url,
      {

        data,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  |
  | Supports:
  |
  | api.delete(url)
  |
  | api.delete(url, true)
  |
  | api.delete(url, {
  |   authentication: true
  | })
  |
  |--------------------------------------------------------------------------
  */

  async delete(
    url,
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "DELETE",
      url,
      {

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | DELETE WITH BODY
  |--------------------------------------------------------------------------
  |
  | Example:
  |
  | api.deleteWithBody(
  |   url,
  |   {
  |     ids: [...]
  |   },
  |   true
  | )
  |
  |--------------------------------------------------------------------------
  */

  async deleteWithBody(
    url,
    data = {},
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "DELETE",
      url,
      {

        data,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | POST URL ENCODED
  |--------------------------------------------------------------------------
  */

  async postUrlEncoded(
    url,
    data = {},
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "POST",
      url,
      {

        data,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

        contentType:
          "application/x-www-form-urlencoded",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | PUT URL ENCODED
  |--------------------------------------------------------------------------
  */

  async putUrlEncoded(
    url,
    data = {},
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "PUT",
      url,
      {

        data,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

        contentType:
          "application/x-www-form-urlencoded",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | PATCH URL ENCODED
  |--------------------------------------------------------------------------
  */

  async patchUrlEncoded(
    url,
    data = {},
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "PATCH",
      url,
      {

        data,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

        contentType:
          "application/x-www-form-urlencoded",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | MULTIPART POST
  |--------------------------------------------------------------------------
  |
  | data must be FormData.
  |
  |--------------------------------------------------------------------------
  */

  async postFormData(
    url,
    formData,
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "POST",
      url,
      {

        data:
          formData,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | MULTIPART PUT
  |--------------------------------------------------------------------------
  */

  async putFormData(
    url,
    formData,
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "PUT",
      url,
      {

        data:
          formData,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | MULTIPART PATCH
  |--------------------------------------------------------------------------
  */

  async patchFormData(
    url,
    formData,
    options = {}
  ) {

    const normalizedOptions =
      this.normalizeRequestOptions(
        options
      );


    return await this.request(
      "PATCH",
      url,
      {

        data:
          formData,

        authentication:
          normalizedOptions
            .authentication,

        tokenKey:
          normalizedOptions
            .tokenKey,

        headers:
          normalizedOptions
            .headers,

        signal:
          normalizedOptions
            .signal,

        credentials:
          normalizedOptions
            .credentials,

        mode:
          normalizedOptions
            .mode,

        cache:
          normalizedOptions
            .cache,

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | GENERIC UPLOAD
  |--------------------------------------------------------------------------
  |
  | Supports:
  |
  | api.upload(
  |   url,
  |   formData,
  |   true
  | )
  |
  | OR:
  |
  | api.upload(
  |   url,
  |   formData,
  |   {
  |     authentication: true,
  |     tokenKey: "accessToken"
  |   }
  | )
  |
  |--------------------------------------------------------------------------
  */

  async upload(
    url,
    formData,
    options = {}
  ) {

    return await this.postFormData(
      url,
      formData,
      options
    );

  }


  /*
  |--------------------------------------------------------------------------
  | DOWNLOAD FILE
  |--------------------------------------------------------------------------
  */

  async download(
    url,
    {
      params = null,
      authentication = false,
      tokenKey = this.tokenKey,
      headers = {},
      signal,
    } = {}
  ) {

    try {

      /*
      |--------------------------------------------------------------------------
      | AUTH
      |--------------------------------------------------------------------------
      */

      const authHeaders =
        await this.buildAuthHeaders({

          authentication,

          tokenKey,

        });


      /*
      |--------------------------------------------------------------------------
      | QUERY
      |--------------------------------------------------------------------------
      */

      const queryString =
        this.buildQueryString(
          params
        );


      /*
      |--------------------------------------------------------------------------
      | FETCH
      |--------------------------------------------------------------------------
      */

      const response =
        await fetch(
          `${url}${queryString}`,
          {

            method:
              "GET",

            headers: {

              ...authHeaders,

              ...headers,

            },

            signal,

          }
        );


      /*
      |--------------------------------------------------------------------------
      | ERROR
      |--------------------------------------------------------------------------
      */

      if (
        !response.ok
      ) {

        const parsedResponse =
          await this.parseResponse(
            response
          );


        /*
        |--------------------------------------------------------------------------
        | Subscription handling also applies to download endpoints
        |--------------------------------------------------------------------------
        */

        this.handleGlobalResponse({

          response:
            parsedResponse,

          url,

        });


        return {

          success:
            false,

          status:
            "failed",

          code:
            response.status,

          message:
            parsedResponse
              ?.message ||
            "Unable to download file.",

          response:
            parsedResponse,

        };

      }


      /*
      |--------------------------------------------------------------------------
      | BLOB
      |--------------------------------------------------------------------------
      */

      const blob =
        await response.blob();


      /*
      |--------------------------------------------------------------------------
      | FILE NAME
      |--------------------------------------------------------------------------
      */

      const contentDisposition =
        response
          .headers
          ?.get(
            "content-disposition"
          ) ||
        "";


      let fileName =
        "download";


      /*
      |--------------------------------------------------------------------------
      | filename*=UTF-8''
      |--------------------------------------------------------------------------
      */

      const utf8Match =
        contentDisposition.match(
          /filename\*=UTF-8''([^;]+)/i
        );


      if (
        utf8Match &&
        utf8Match[
          1
        ]
      ) {

        try {

          fileName =
            decodeURIComponent(
              utf8Match[
                1
              ]
            );

        } catch (
          error
        ) {

          fileName =
            utf8Match[
              1
            ];

        }

      } else {

        /*
        |--------------------------------------------------------------------------
        | filename="..."
        |--------------------------------------------------------------------------
        */

        const normalMatch =
          contentDisposition.match(
            /filename="?([^"]+)"?/i
          );


        if (
          normalMatch &&
          normalMatch[
            1
          ]
        ) {

          fileName =
            normalMatch[
              1
            ];

        }

      }


      return {

        success:
          true,

        status:
          "success",

        code:
          response.status,

        blob,

        fileName,

        contentType:
          response
            .headers
            ?.get(
              "content-type"
            ) ||
          "",

      };

    } catch (
      error
    ) {

      if (
        error
          ?.name ===
        "AbortError"
      ) {

        return {

          success:
            false,

          status:
            "cancelled",

          code:
            0,

          message:
            "Download cancelled.",

        };

      }


      console.error(
        "Download error:",
        error
      );


      return {

        success:
          false,

        status:
          "failed",

        code:
          0,

        message:
          error
            ?.message ||
          "Unable to download file.",

        error,

      };

    }

  }


  /*
  |--------------------------------------------------------------------------
  | DOWNLOAD AND SAVE
  |--------------------------------------------------------------------------
  |
  | Browser helper.
  |
  | Example:
  |
  | await api.downloadAndSave(
  |   url,
  |   {
  |     authentication: true
  |   }
  | );
  |
  |--------------------------------------------------------------------------
  */

  async downloadAndSave(
    url,
    options = {}
  ) {

    const result =
      await this.download(
        url,
        options
      );


    if (
      result
        ?.success !==
      true
    ) {

      return result;

    }


    /*
    |--------------------------------------------------------------------------
    | SSR SAFETY
    |--------------------------------------------------------------------------
    */

    if (
      typeof window ===
        "undefined" ||
      typeof document ===
        "undefined"
    ) {

      return result;

    }


    /*
    |--------------------------------------------------------------------------
    | CREATE TEMP URL
    |--------------------------------------------------------------------------
    */

    const objectUrl =
      window
        .URL
        .createObjectURL(
          result.blob
        );


    /*
    |--------------------------------------------------------------------------
    | CREATE LINK
    |--------------------------------------------------------------------------
    */

    const link =
      document
        .createElement(
          "a"
        );


    link.href =
      objectUrl;


    link.download =
      result.fileName ||
      "download";


    document
      .body
      .appendChild(
        link
      );


    link.click();


    /*
    |--------------------------------------------------------------------------
    | CLEANUP
    |--------------------------------------------------------------------------
    */

    document
      .body
      .removeChild(
        link
      );


    window
      .URL
      .revokeObjectURL(
        objectUrl
      );


    return result;

  }


  /*
  |--------------------------------------------------------------------------
  | PLATFORM GET
  |--------------------------------------------------------------------------
  |
  | Convenience helper.
  |
  |--------------------------------------------------------------------------
  */

  async platformGet(
    url,
    options = {}
  ) {

    return await this.get(
      url,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "platformAccessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | PLATFORM POST
  |--------------------------------------------------------------------------
  */

  async platformPost(
    url,
    data = {},
    options = {}
  ) {

    return await this.post(
      url,
      data,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "platformAccessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | PLATFORM PUT
  |--------------------------------------------------------------------------
  */

  async platformPut(
    url,
    data = {},
    options = {}
  ) {

    return await this.put(
      url,
      data,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "platformAccessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | PLATFORM PATCH
  |--------------------------------------------------------------------------
  */

  async platformPatch(
    url,
    data = {},
    options = {}
  ) {

    return await this.patch(
      url,
      data,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "platformAccessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | PLATFORM DELETE
  |--------------------------------------------------------------------------
  */

  async platformDelete(
    url,
    options = {}
  ) {

    return await this.delete(
      url,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "platformAccessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | TENANT GET
  |--------------------------------------------------------------------------
  |
  | Convenience helper.
  |
  |--------------------------------------------------------------------------
  */

  async tenantGet(
    url,
    options = {}
  ) {

    return await this.get(
      url,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "accessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | TENANT POST
  |--------------------------------------------------------------------------
  */

  async tenantPost(
    url,
    data = {},
    options = {}
  ) {

    return await this.post(
      url,
      data,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "accessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | TENANT PUT
  |--------------------------------------------------------------------------
  */

  async tenantPut(
    url,
    data = {},
    options = {}
  ) {

    return await this.put(
      url,
      data,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "accessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | TENANT PATCH
  |--------------------------------------------------------------------------
  */

  async tenantPatch(
    url,
    data = {},
    options = {}
  ) {

    return await this.patch(
      url,
      data,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "accessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | TENANT DELETE
  |--------------------------------------------------------------------------
  */

  async tenantDelete(
    url,
    options = {}
  ) {

    return await this.delete(
      url,
      {

        ...(
          typeof options ===
          "object"
            ?
            options
            :
            {}
        ),

        authentication:
          true,

        tokenKey:
          "accessToken",

      }
    );

  }


  /*
  |--------------------------------------------------------------------------
  | SET TOKEN KEY
  |--------------------------------------------------------------------------
  |
  | Optional:
  |
  | const api = new API();
  |
  | api.setTokenKey(
  |   "platformAccessToken"
  | );
  |
  |--------------------------------------------------------------------------
  */

  setTokenKey(
    tokenKey
  ) {

    if (
      tokenKey
    ) {

      this.tokenKey =
        tokenKey;

    }


    return this;

  }


  /*
  |--------------------------------------------------------------------------
  | GET CURRENT TOKEN KEY
  |--------------------------------------------------------------------------
  */

  getTokenKey() {

    return this.tokenKey;

  }


  /*
  |--------------------------------------------------------------------------
  | HAS TOKEN
  |--------------------------------------------------------------------------
  */

  async hasToken(
    tokenKey =
      this.tokenKey
  ) {

    if (
      typeof window ===
      "undefined"
    ) {

      return false;

    }


    const token =
      localStorage
        .getItem(
          tokenKey
        )
        ?.trim();


    return Boolean(
      token
    );

  }


  /*
  |--------------------------------------------------------------------------
  | CLEAR TOKEN
  |--------------------------------------------------------------------------
  */

  clearToken(
    tokenKey =
      this.tokenKey
  ) {

    if (
      typeof window ===
      "undefined"
    ) {

      return;

    }


    localStorage
      .removeItem(
        tokenKey
      );

  }


  /*
  |--------------------------------------------------------------------------
  | SET TOKEN
  |--------------------------------------------------------------------------
  */

  setToken(
    token,
    tokenKey =
      this.tokenKey
  ) {

    if (
      typeof window ===
      "undefined"
    ) {

      return;

    }


    if (
      !token
    ) {

      localStorage
        .removeItem(
          tokenKey
        );


      return;

    }


    localStorage
      .setItem(
        tokenKey,
        token
      );

  }

}


/*
|--------------------------------------------------------------------------
| DEFAULT API
|--------------------------------------------------------------------------
|
| Existing application:
|
| const api = new API();
|
| uses:
|
| accessToken
|
|--------------------------------------------------------------------------
*/

export default API;


/*
|--------------------------------------------------------------------------
| OPTIONAL PRECONFIGURED INSTANCES
|--------------------------------------------------------------------------
|
| You do NOT need to use these if your current code already does:
|
| const api = new API();
|
| They are provided for cleaner future usage.
|
|--------------------------------------------------------------------------
*/

export const tenantAPI =
  new API({
    tokenKey:
      "accessToken",
  });


export const platformAPI =
  new API({
    tokenKey:
      "platformAccessToken",
  });